import React from 'react';

const DisplayCanvasEvents = ({ events, canvasUrl, onRefresh, showInput, onLoadEvents, setCanvasUrl }) => {
    const getCanvasCalendarUrl = () => {
        if (!canvasUrl) return 'https://mvla.instructure.com/calendar';
        const match = canvasUrl.match(/(https:\/\/[^/]+)/);
        return match ? `${match[1]}/calendar` : 'https://mvla.instructure.com/calendar';
    };

    const getCalendarFeedInstructions = () => {
        window.open('https://community.instructure.com/en/kb/articles/662804-how-do-i-view-the-calendar-ical-feed-to-import-and-subscribe-to-an-external-calendar', '_blank');
    };

    const formatDate = (date) => {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        const options = { weekday: 'short', month: 'numeric', day: 'numeric' };
        return dateObj.toLocaleDateString('en-US', options);
    };

    const formatTime = (date) => {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dateObj.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const isToday = (date) => {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        const today = new Date();
        return dateObj.getDate() === today.getDate() &&
            dateObj.getMonth() === today.getMonth() &&
            dateObj.getFullYear() === today.getFullYear();
    };

    const isTomorrow = (date) => {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return dateObj.getDate() === tomorrow.getDate() &&
            dateObj.getMonth() === tomorrow.getMonth() &&
            dateObj.getFullYear() === tomorrow.getFullYear();
    };

    const getDateLabel = (date) => {
        if (isToday(date)) return 'Today';
        if (isTomorrow(date)) return 'Tomorrow';
        return formatDate(date);
    };

    const todayEvents = events ? events.filter(event => isToday(event.startDate)) : [];
    const upcomingEvents = events ? events.filter(event => !isToday(event.startDate)) : [];

    const renderEvent = (event, index) => (
        <div
            key={index}
            className={`canvas-event-item ${event.isSubmitted ? 'submitted' : ''}`}
        >
            <div className="event-date-badge">
                {getDateLabel(event.startDate)}
            </div>
            <div className="event-content">
                <div className="event-header">
                    {event.url ? (
                        <a
                            href={event.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`event-title-link ${event.isSubmitted ? 'submitted-link' : ''}`}
                        >
                            {event.title}
                            {event.isSubmitted && <span className="submitted-badge"> ✓ Submitted</span>}
                        </a>
                    ) : (
                        <span className={`event-title ${event.isSubmitted ? 'submitted-text' : ''}`}>
                            {event.title}
                            {event.isSubmitted && <span className="submitted-badge"> ✓ Submitted</span>}
                        </span>
                    )}
                </div>
                <div className="event-time">
                    {formatTime(event.startDate)}
                    {event.endDate && event.endDate !== event.startDate && (
                        <> - {formatTime(event.endDate)}</>
                    )}
                </div>
                {event.courseInfo && (
                    <div className="event-course-info">
                        {event.courseInfo.period && (
                            <span className="course-period">Period {event.courseInfo.period}</span>
                        )}
                        {event.courseInfo.teacher && (
                            <span className="course-teacher">{event.courseInfo.teacher}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="canvas-events-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Canvas Assignments</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {!showInput && onRefresh && (
                        <button
                            onClick={onRefresh}
                            className="canvas-action-button"
                        >
                            Refresh
                        </button>
                    )}
                    {/* View Calendar button - now always shows */}
                    <button
                        onClick={() => window.open(getCanvasCalendarUrl(), '_blank')}
                        className="canvas-action-button"
                    >
                        View Calendar
                    </button>
                    {showInput && (
                        <button
                            onClick={getCalendarFeedInstructions}
                            className="canvas-action-button secondary"
                        >
                            Calendar Feed Help
                        </button>
                    )}
                </div>
            </div>

            {/* Canvas URL Input - show if not configured */}
            {showInput && (
                <div style={{ marginBottom: '20px' }}>
                    <input
                        type="text"
                        placeholder="Paste your Canvas calendar URL"
                        value={canvasUrl}
                        onChange={(e) => setCanvasUrl(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '4px',
                            border: '1px solid #4a5158',
                            backgroundColor: '#2a2f37',
                            color: 'white',
                            marginBottom: '10px'
                        }}
                    />
                    <button
                        onClick={onLoadEvents}
                        className="canvas-action-button"
                        style={{ width: '100%' }}
                    >
                        Load Canvas Events
                    </button>
                </div>
            )}

            {/* Show events or "no assignments" */}
            {!showInput ? (
                <>
                    <div className="events-section">
                        <h3 className="section-title">Due Today</h3>
                        <div className="events-list">
                            {todayEvents.length > 0 ? (
                                todayEvents.map((event, index) => renderEvent(event, index))
                            ) : (
                                <p style={{ color: '#9e9e9e', margin: '8px 0' }}>None</p>
                            )}
                        </div>
                    </div>

                    <div className="events-section">
                        <h3 className="section-title">Upcoming</h3>
                        <div className="events-list">
                            {upcomingEvents.length > 0 ? (
                                upcomingEvents.map((event, index) => renderEvent(event, index))
                            ) : (
                                <p style={{ color: '#9e9e9e', margin: '8px 0' }}>None</p>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <p style={{ color: '#9e9e9e', textAlign: 'center' }}>
                    Enter your Canvas calendar URL to view assignments
                </p>
            )}
        </div>
    );
};

export default DisplayCanvasEvents;